import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Building2, Loader2, Mail, Lock, User, Eye, EyeOff, Home, Key, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { validatePersonFullName } from '../lib/personNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INVITE_ACCEPTED_STORAGE_KEY = 'siyak_invite_accepted';

export const Register = () => {
  useEffect(() => { document.title = 'Create Account - SIYAK'; }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { register, refreshUser } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get('invite_token');
  const inviteEmail = queryParams.get('invite_email') || '';
  const requestedRole = (queryParams.get('role') || '').toLowerCase();
  const initialRole = inviteToken ? 'tenant' : requestedRole === 'landlord' ? 'landlord' : 'tenant';
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(initialRole);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingRole, setPendingRole] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const fullName = validatePersonFullName(name);
    if (!fullName.valid) {
      toast.error(fullName.error);
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (inviteToken && role !== 'tenant') {
      toast.error('Invited users must register as tenants');
      return;
    }

    setLoading(true);

    try {
      const result = await register(fullName.value, email, password, role, inviteToken);

      if (result?.requires_email_verification) {
        setPendingEmail(result.email || email);
        setPendingRole(role);
        return;
      }

      const user = result;
      const normalizedRole = String(user?.role || '').toLowerCase();

      if (inviteToken && normalizedRole === 'tenant') {
        try {
          const acceptResponse = await axios.post(`${API_URL}/api/invites/accept`, { token: inviteToken });
          toast.success('Invite accepted. Opening your tenancy dashboard.');
          try {
            sessionStorage.setItem(INVITE_ACCEPTED_STORAGE_KEY, JSON.stringify({
              propertyTitle: acceptResponse.data?.property_title || user?.property_title || 'your property',
              acceptedAt: new Date().toISOString(),
            }));
          } catch (error) {}
        } catch (inviteError) {
          const detail = inviteError.response?.data?.detail;
          if (detail) {
            toast.error(detail);
          } else {
            toast.error('Account created, but invite acceptance failed');
          }
        } finally {
          await refreshUser();
        }
      }

      if (role === 'landlord' && !user.is_approved) {
        toast.success('Account created! Your landlord account is pending admin approval.');
      } else {
        toast.success(`Welcome to SIYAK, ${user.name}!`);
      }

      // Redirect based on role
      switch (normalizedRole) {
        case 'landlord':
          navigate('/landlord');
          break;
        case 'tenant':
          navigate('/tenant');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (pendingEmail) {
    return (
      <Layout hideFooter>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-navy-50 px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
                <Building2 className="h-10 w-10 text-royal-600" />
              </Link>
              <CardTitle className="font-heading text-2xl">Check your email</CardTitle>
              <CardDescription>We've sent a verification link to</CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div className="flex flex-col items-center gap-3">
                <CheckCircle className="h-12 w-12 text-green-600" />
                <p className="font-medium text-navy-800">{pendingEmail}</p>
                <p className="text-sm text-muted-foreground">
                  Click the link in the email to activate your account. The link expires in 24 hours.
                </p>
                {pendingRole === 'landlord' && (
                  <p className="text-sm text-muted-foreground">
                    After you verify your email, we&apos;ll review your landlord account and get back to you about approval as soon as possible.
                  </p>
                )}
                {inviteToken && pendingRole === 'tenant' && (
                  <p className="text-sm text-blue-800 rounded-md border border-blue-200 bg-blue-50 p-3">
                    Your property invite is saved. After email verification, we&apos;ll accept the invite and take you straight to your tenancy dashboard.
                  </p>
                )}
              </div>
              <div className="pt-2 text-sm text-muted-foreground">
                Didn't receive it?{' '}
                <button
                  className="text-royal-600 hover:underline font-medium"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: pendingEmail });
                      toast.success('Verification email resent');
                    } catch {
                      toast.error('Failed to resend. Please try again shortly.');
                    }
                  }}
                >
                  Resend email
                </button>
              </div>
              <div className="pt-1">
                <Link to="/login" className="text-sm text-royal-600 hover:underline font-medium">
                  Back to login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-navy-50 px-4 py-12" data-testid="register-page">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
              <Building2 className="h-10 w-10 text-royal-600" />
            </Link>
            <CardTitle className="font-heading text-2xl">Create Account</CardTitle>
            <CardDescription>
              Join SIYAK as a tenant or landlord
            </CardDescription>
          </CardHeader>
          <CardContent>
            {inviteToken && (
              <div className="mb-4 p-4 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="invite-register-banner">
                You were invited to join a property. Register as a tenant to accept the invite.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Role Selection */}
              <div className="space-y-4">
                <Label>I am a...</Label>
                <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-4">
                  <div>
                    <RadioGroupItem value="tenant" id="tenant" className="peer sr-only" />
                    <Label
                      htmlFor="tenant"
                      className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-royal-600 [&:has([data-state=checked])]:border-royal-600 cursor-pointer"
                      data-testid="role-tenant"
                    >
                      <Home className="h-6 w-6 mb-2" />
                      <span className="font-medium">Tenant</span>
                      <span className="text-xs text-muted-foreground">Looking to rent</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="landlord" id="landlord" className="peer sr-only" />
                    <Label
                      htmlFor="landlord"
                      className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-royal-600 [&:has([data-state=checked])]:border-royal-600 cursor-pointer"
                      style={{ opacity: inviteToken ? 0.5 : 1, pointerEvents: inviteToken ? 'none' : 'auto' }}
                      data-testid="role-landlord"
                    >
                      <Key className="h-6 w-6 mb-2" />
                      <span className="font-medium">Landlord</span>
                      <span className="text-xs text-muted-foreground">List properties</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Full Name (e.g. John Smith)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    readOnly={Boolean(inviteEmail)}
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              {role === 'landlord' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                  Landlord accounts require admin approval before you can list properties.
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-royal-600 hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
